import { formatRating } from "@/lib/rating-utils";
import { scoreBand } from "@/lib/scorecard";

type StampSize = "xs" | "sm" | "md" | "lg" | "xl";

const SIZE_CLASSES: Record<StampSize, string> = {
  xs: "min-w-[34px] px-1.5 py-0.5 text-[15px]",
  sm: "min-w-[42px] px-2 py-1 text-[19px]",
  md: "min-w-[58px] px-2.5 py-1.5 text-[28px]",
  lg: "min-w-[96px] px-4 py-2 text-[52px]",
  xl: "min-w-[120px] px-5 py-2.5 text-[72px]",
};

/** Background/foreground per score band; ink text keeps contrast on every fill. */
export function stampColors(rating: number): string {
  if (rating >= 8.5) return "bg-[#ffd23f] text-[#0d0c0a]";
  if (rating >= 7.5) return "bg-[#ff6a33] text-[#0d0c0a]";
  if (rating >= 6.5) return "bg-[#f4efe6] text-[#0d0c0a]";
  if (rating >= 5.5) return "bg-[#a89f90] text-[#0d0c0a]";
  return "bg-[#3a3530] text-[#f4efe6]";
}

interface ScoreStampProps {
  rating: number;
  size?: StampSize;
  /** Show the band label ("Essential", "Great"…) under the number. */
  showBand?: boolean;
  /** Editorial estimate (album not reviewed in full yet): prefixes "~". */
  estimate?: boolean;
  className?: string;
}

/** Poster-style score block used on covers, heroes and lists. */
export function ScoreStamp({ rating, size = "sm", showBand = false, estimate = false, className = "" }: ScoreStampProps) {
  const band = scoreBand(rating).label;
  return (
    <span
      className={`inline-flex flex-col items-center justify-center leading-none ${stampColors(rating)} ${className}`}
      title={estimate ? `~${formatRating(rating)}/10 · editorial estimate, not yet reviewed in full` : `${formatRating(rating)}/10 · ${band}`}
    >
      <span className={`font-display tabular-nums leading-[0.95] ${SIZE_CLASSES[size]}`}>
        {estimate && <span className="opacity-60" aria-label="estimated">~</span>}
        {formatRating(rating)}
      </span>
      {showBand && (
        <span className="w-full border-t border-black/15 px-2 py-1 text-center text-[10px] font-extrabold uppercase tracking-[0.16em]">
          {band}
        </span>
      )}
    </span>
  );
}
